import { DataEditor, GridCell, GridCellKind, GridColumn, GridSelection, Item } from '@glideapps/glide-data-grid';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ExcelData,
  LiquidacionAsincronaEstado,
  LiquidacionAsincronaV2Details,
} from '../../../../../system/liquidacion_asincrona/LiquidacionAsincronaV2System';
import { DefaultPalette, PrimaryButton, Spinner } from '@fluentui/react';
import { t } from 'i18next';
import TimeTaxSupportSystemClient from '../../../../../system';
import MessageCourier from '../../../../../lib/MessageCourier';
import { useNavigate } from 'react-router-dom';
import { routes } from '../../../../../common/constants';
import { handleColumnResizeUtil } from '../../../../../lib/GlideDataGrid/UtilsGlideGrid';

interface SettlementTablePreliquidadoAjeProps {
  settlement: LiquidacionAsincronaV2Details;
  system: TimeTaxSupportSystemClient;
  courier: MessageCourier;
  changeBlockUI: (v: boolean) => void;
  id: string;
}

interface ColumnConfig {
  title: string;
  editable?: boolean;
  width?: number;
}
/* 
[
    "UUID",
    "EJERCICIO",
    "PERIODO",
    "TIPO_DE_INFORMACION",
    "COMPANIA",
    "COMPANIA_",
    "CONCEPTO",
    "DETALLE",
    "DETALLE_",
    "Doc_Identidad",
    "Persona",
    "CostoXHora",
    "HORAS",
    "IMPORTE_DE_HRS_SIN_MARGEN",
    "IMPORTE_DE_HRS_CON_MARGEN",
    "IMPORTE_DE_GASTOS_SIN_MARGEN",
    "IMPORTE_DE_GASTOS_CON_MARGEN",
    "IMPORTE_TOTAL_SIN_MARGEN",
    "IMPORTE_TOTAL_CON_MARGEN",
    "MARGEN_APLICADO",
    "MARGEN_APLICADO_PORCENTAJE",
    "DETALLE_COMPANIA_CANONICO_BINDING",
    "DETALLE_COMPANIA_CANONICO_EMBEDING",
    "DETALLE_COMPANIA_EMBEDING"
  ],
 */
const columnConfig: Record<string, ColumnConfig> = {
  EJERCICIO: { title: 'Ejercicio' },
  PERIODO: { title: 'Periodo' },
  COMPANIA: { title: 'Compañía (*)', editable: true },
  COMPANIA_: { title: 'Compañía_' },
  CONCEPTO: { title: 'Concepto' },

  DETALLE: { title: 'Detalle (*)', editable: true },
  DETALLE_: { title: 'Detalle_' },

  Doc_Identidad: { title: 'Doc. Identidad' },
  Persona: { title: 'Persona' },

  CostoXHora: { title: 'Costo x Hora' },

  HORAS: { title: 'Horas' },

  IMPORTE_DE_HRS_SIN_MARGEN: { title: 'Importe horas sin margen' },
  IMPORTE_DE_HRS_CON_MARGEN: { title: 'Importe horas con margen' },

  IMPORTE_DE_GASTOS_SIN_MARGEN: { title: 'Importe gastos sin margen' },
  IMPORTE_DE_GASTOS_CON_MARGEN: { title: 'Importe gastos con margen' },

  IMPORTE_TOTAL_SIN_MARGEN: { title: 'Importe total sin margen' },
  IMPORTE_TOTAL_CON_MARGEN: { title: 'Importe total con margen' },

  MARGEN_APLICADO_PORCENTAJE: { title: 'Margen aplicado (%)' },

  DETALLE_COMPANIA_CANONICO_BINDING: { title: 'Detalle compañía canónico binding', width: 300 },
  DETALLE_COMPANIA_CANONICO_EMBEDING: { title: 'Detalle compañía canónico embedding', width: 300 },
  DETALLE_COMPANIA_EMBEDING: { title: 'Detalle compañía embedding', width: 300 },
};

type HistoryAction = {
  rowId: string; // El UUID de la fila
  column: string;
  oldValue: any;
};

export default function SettlementTablePreliquidadoAje({ id, system, courier, settlement, changeBlockUI }: SettlementTablePreliquidadoAjeProps) {
  const [loading, setLoading] = useState(true);
  const [gridSelection, setGridSelection] = useState<GridSelection | undefined>(undefined);
  const [rows, setRows] = useState<ExcelData['rows']>([]);
  const [importeTotal, setImporteTotal] = useState<string>('');
  // Dentro de tu componente SettlementTablePreliquidadoAje
  const [history, setHistory] = useState<HistoryAction[][]>([]);
  const [columns, setColumns] = useState<GridColumn[]>([]);

  // Al inicio de tu componente
  const isPasting = useRef(false);

  const columnKeys = useMemo(() => Object.keys(columnConfig), []);

  useEffect(() => {
    const initialColumns: GridColumn[] = columnKeys.map((key) => ({
      title: columnConfig[key].title,
      width: columnConfig[key].width ?? 180,
    }));
    setColumns(initialColumns);
  }, [columnKeys]);

  const navigate = useNavigate();

  const undo = useCallback(() => {
    if (history.length === 0) return;

    const lastGroup = history[history.length - 1]; // Es un array de acciones
    const newHistory = history.slice(0, -1);

    setRows((currentRows) => {
      // Creamos un mapa para búsquedas rápidas en este undo
      const changeMap = new Map();
      lastGroup.forEach((action) => {
        changeMap.set(`${action.rowId}-${action.column}`, action.oldValue);
      });

      return currentRows.map((row) => {
        const updatedRow = { ...row };
        let hasChanged = false;

        // Verificamos si alguna columna de esta fila debe volver atrás
        Object.keys(columnConfig).forEach((col) => {
          const key = `${row.UUID}-${col}`;
          if (changeMap.has(key)) {
            updatedRow[col] = changeMap.get(key);
            hasChanged = true;
          }
        });

        return hasChanged ? updatedRow : row;
      });
    });

    setHistory(newHistory);
  }, [history]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Si la liquidación está en estado PENDIENTE, obtenemos el reporte de revisión para mostrarlo en la tabla
        if (settlement.liquidacion.lidasinc_estado === LiquidacionAsincronaEstado.PENDIENTE) {
          const reporteRevision = await system.getLiquidacionAsincronaV2System().getReporteRevision(id);
          if (reporteRevision) {
            // Filtramos solo las filas normales (sin la fila de totales) para mostrarlas en la tabla
            setRows(reporteRevision.rows.filter((r) => r.UUID !== 'TOTALES'));

            // Obtenemos el importe total del reporte de revisión para mostrarlo en el detalle y usarlo al reprocesar la liquidación
            const totalesRow = reporteRevision.rows.find((r) => r.UUID === 'TOTALES');
            if (totalesRow) {
              setImporteTotal(totalesRow['IMPORTE_TOTAL_CON_MARGEN']);
            }
          } else {
            courier.messageError(t('No se pudo cargar el reporte de revisión'));
          }
        }
      } catch (error) {
        console.log(error);
        courier.messageError(t('Error al cargar el reporte de revisión'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, settlement]);

  const editableColumns = columnKeys.map((key, idx) => (columnConfig[key].editable ? idx : -1)).filter((idx) => idx !== -1);

  // Validación para habilitar/deshabilitar el botón Reprocesar
  const isReprocesarEnabled = useMemo(() => {
    return rows.every((row) => {
      for (const colKey of columnKeys) {
        if (columnConfig[colKey].editable) {
          const value = String(row[colKey] ?? '').trim();

          if (!value || (colKey === 'DETALLE' && value.toUpperCase() === 'REVISAR')) {
            return false;
          }
        }
      }
      return true;
    });
  }, [rows, columnKeys]);

  // Obtener contenido de cada celda
  const getCellContent = useCallback(
    ([col, row]: Item): GridCell => {
      const colName = columnKeys[col];

      const value = rows[row][colName] ?? '';

      const isEditable = editableColumns.includes(col);
      return {
        kind: GridCellKind.Text,
        data: value,
        displayData: value,
        readonly: !isEditable,
        allowOverlay: isEditable,
      };
    },
    [rows, editableColumns, columnKeys],
  );

  const handleReprocesar = async () => {
    const payload = rows.map((r) => ({
      UUID: r.UUID,
      COMPANIA: r.COMPANIA,
      DETALLE: r.DETALLE,
    }));

    try {
      changeBlockUI(true);
      await system.getLiquidacionAsincronaV2System().actualizarReporte(id, payload, importeTotal);
      courier.messageSuccess(t('Reporte actualizado correctamente'));
    } catch (error) {
      console.error('Error al actualizar reporte:', error);
      courier.messageError(t('Error al actualizar el reporte'));
    } finally {
      changeBlockUI(false);
      navigate(routes.direct_settlement_list_aje);
    }
  };

  const onCellEdited = useCallback(
    ([col, rowIdx]: Item, newValue: GridCell) => {
      // Si estamos pegando, ignoramos esta ejecución individual
      if (isPasting.current || newValue.kind !== GridCellKind.Text) return;

      const colName = columnKeys[col];
      const targetRow = rows[rowIdx];
      const valorActual = targetRow[colName];
      const valorNuevo = newValue.data;

      if (valorActual === valorNuevo) return;

      const action: HistoryAction = {
        rowId: targetRow.UUID,
        column: colName,
        oldValue: valorActual,
      };

      setHistory((prev) => [...prev, [action]].slice(-100)); // Guardamos como un grupo de 1

      setRows((prev) => {
        const updated = [...prev];
        updated[rowIdx] = { ...updated[rowIdx], [colName]: valorNuevo };
        return updated;
      });
    },
    [columnKeys, rows],
  );

  const handleCopy = useCallback(() => {
    const range = gridSelection?.current?.range;
    if (!range) return;

    const { x, y, width, height } = range;

    const result: string[][] = [];

    for (let row = y; row < y + height; row++) {
      const rowData: string[] = [];

      for (let col = x; col < x + width; col++) {
        const colKey = columnKeys[col];
        if (!colKey) continue;

        rowData.push(String(rows[row]?.[colKey] ?? ''));
      }

      result.push(rowData);
    }

    const text = result.map((r) => r.join('\t')).join('\n');

    navigator.clipboard.writeText(text);
  }, [gridSelection, rows, columnKeys]);

  const handlePaste = useCallback(
    (target: Item, values: readonly (readonly string[])[]): boolean => {
      const range = gridSelection?.current?.range;
      if (!range) return false;

      const { x, y, width, height } = range;
      const bulkActions: HistoryAction[] = [];

      // BLOQUEO INMEDIATO
      isPasting.current = true;

      // Calculamos basándonos en el estado actual 'rows'
      const newRows = [...rows];

      for (let rowOffset = 0; rowOffset < height; rowOffset++) {
        const targetRowIdx = y + rowOffset;
        if (!newRows[targetRowIdx]) continue;

        for (let colOffset = 0; colOffset < width; colOffset++) {
          const targetColIdx = x + colOffset;
          const colKey = columnKeys[targetColIdx];
          if (!colKey || !columnConfig[colKey].editable) continue;

          const value = values[rowOffset]?.[colOffset] ?? values[0]?.[0] ?? '';
          const valorActual = newRows[targetRowIdx][colKey];

          if (valorActual !== value) {
            bulkActions.push({
              rowId: newRows[targetRowIdx].UUID,
              column: colKey,
              oldValue: valorActual,
            });
            newRows[targetRowIdx] = { ...newRows[targetRowIdx], [colKey]: value };
          }
        }
      }

      if (bulkActions.length > 0) {
        // Actualizamos historial y filas por separado
        setHistory((prev) => [...prev, bulkActions].slice(-100));
        setRows(newRows);
      }

      // El timeout debe ser suficiente para que el evento nativo del Grid termine
      setTimeout(() => {
        isPasting.current = false;
      }, 200);

      return true;
    },
    [gridSelection, columnKeys, rows],
  );

  const handleColumnResize = useCallback(handleColumnResizeUtil(setColumns), [setColumns]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isZ = e.key.toLowerCase() === 'z';
      const isC = e.key.toLowerCase() === 'c';
      const isCtrl = e.ctrlKey || e.metaKey;

      if (isCtrl && isC) {
        e.preventDefault();
        handleCopy();
      }

      if (isCtrl && isZ) {
        e.preventDefault();
        undo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleCopy, undo]);

  if (loading) {
    return <Spinner label={t('Cargando reporte...')} />;
  }

  if (rows.length === 0) {
    return <div>{t('No hay datos para mostrar')}</div>;
  }

  console.log({ history });

  return (
    <>
      {/* FOOTER */}
      <div className="detail-footer-aje">
        <PrimaryButton
          text={t('Reprocesar')}
          iconProps={{ iconName: 'Refresh', color: DefaultPalette.themeLighterAlt }}
          onClick={handleReprocesar}
          disabled={!isReprocesarEnabled}
        />
      </div>

      <div style={{ height: '600px' }}>
        <DataEditor
          columns={columns}
          rows={rows.length}
          getCellContent={getCellContent}
          onCellEdited={onCellEdited}
          onGridSelectionChange={setGridSelection}
          gridSelection={gridSelection}
          onPaste={handlePaste}
          onColumnResize={handleColumnResize}
          rowMarkers="none"
          columnSelect="multi"
          smoothScrollX
          smoothScrollY
        />
      </div>
    </>
  );
}
